import React from 'react';
import { GenericFormPage } from '../../components/generic';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { usePackage, Package, usePackageTypesForSelect } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const { options: packageTypeOptions } = usePackageTypesForSelect();
  const configWithOptions = (() => {
    const fields = moduleConfig.fields.map((field: FieldConfig) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'packageTypeId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Package Type',
          placeholder: 'Select package type (optional)',
          options: [{ value: '', label: 'None' }, ...packageTypeOptions],
        };
      }
      if (field.name === 'packageCategoryId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Category',
          placeholder: 'Select category (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-crm-service-category',
          labelField: 'psc_descr',
          valueField: 'id' as const,
        };
      }
      return field;
    });

    // Form data allows foreign keys as string for select components
    type FormPackage = Omit<Package, 'clientId' | 'packageTypeId' | 'packageCategoryId'> & {
      clientId?: string | number;
      packageTypeId?: string | number;
      packageCategoryId?: string | number;
    };

    // Custom mapItemToFormData that converts foreign keys to string for select
    const customMapItemToFormData = (item: Package): Partial<FormPackage> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        packageTypeId: baseData.packageTypeId ? String(baseData.packageTypeId) : '',
        packageCategoryId: baseData.packageCategoryId ? String(baseData.packageCategoryId) : '',
      };
    };

    // Custom getInitialFormData
    const customGetInitialFormData = (): Partial<FormPackage> => {
      return {
        ...getInitialFormData(),
        clientId: '',
        packageTypeId: '',
        packageCategoryId: '',
      };
    };

    // Helper function to convert string to number or undefined
    const convertToNumber = (value: string | number | undefined): number | undefined => {
      if (value === undefined || value === null || String(value) === '') {
        return undefined;
      }
      return Number(value);
    };

    // Helper function to convert date-only strings to ISO-8601 datetime strings
    const convertDateToDateTime = (value: string | undefined): string | undefined => {
      if (value === undefined || value === null || String(value).trim() === '') {
        return undefined;
      }
      const dateStr = String(value).trim();
      
      if (dateStr.includes('T') || dateStr.includes('Z')) {
        return dateStr;
      }
      
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const date = new Date(dateStr + 'T00:00:00.000Z');
        return date.toISOString();
      }
      
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      } catch {
        // If parsing fails, return the original value
      }
      
      return dateStr;
    };

    // Custom create/update handlers that convert foreign key strings back to numbers
    const customCreate = async (data: Partial<FormPackage>) => {
      const processedData: Partial<Package> = {
        ...data,
        clientId: convertToNumber(data.clientId) as number, // Required field
        packageTypeId: convertToNumber(data.packageTypeId),
        packageCategoryId: convertToNumber(data.packageCategoryId),
        packageStartDate: convertDateToDateTime(data.packageStartDate),
        packageEndDate: convertDateToDateTime(data.packageEndDate),
      };
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormPackage>) => {
      const processedData: Partial<Package> = {
        ...data,
        clientId: convertToNumber(data.clientId) as number, // Required field
        packageTypeId: convertToNumber(data.packageTypeId),
        packageCategoryId: convertToNumber(data.packageCategoryId),
        packageStartDate: convertDateToDateTime(data.packageStartDate),
        packageEndDate: convertDateToDateTime(data.packageEndDate),
      };
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<Package>,
      mapItemToFormData: customMapItemToFormData as (item: Package) => Partial<Package>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<Package>) => Promise<Package>,
        update: customUpdate as (id: number, data: Partial<Package>) => Promise<Package>,
      },
    };
  })();

  return (
    <GenericFormPage<Package>
      useData={usePackage}
      config={configWithOptions}
    />
  );
};

export default Form;

